import {toast} from "react-toastify";
import {DocumentRunnerStatus} from "../../entity/enums/document-runner-status";
import {ProgressStatusDto} from "../../entity/dto/progress-status-dto";
import {ValidateDocumentStatus} from "../../entity/enums/validate-document-status";
import {SplitDocumentsStatus} from "../../entity/enums/split-documents-status";
import {MergeDocumentsStatus} from "../../entity/enums/merge-documents-status";
import {MarketplaceOperationStatus} from "../../entity/enums/marketplace-operation-status";
import AuditLogsStore from "../store/audit-logs-store";
import {ValidateDocumentDto} from "../../entity/dto/validate-document-dto";

export default class AuditLogsService {
    auditLogsStore: AuditLogsStore;

    constructor(auditLogsStore: AuditLogsStore) {
        this.auditLogsStore = auditLogsStore;
    }

    getDocumentRunnerLogs = async (organizationId: string, batchId: string): Promise<ProgressStatusDto | undefined> => {
        const response = await this.auditLogsStore.getAuditLogs(organizationId, batchId);

        if (response && typeof response !== 'string' && response.length > 0) {
            const status = response[0].message.split('#')[0];

            switch (status) {
                case DocumentRunnerStatus.DOCUMENT_RUNNER_INITIATED:
                    return { progress: 0, message: 'Your request is being processed' };

                case DocumentRunnerStatus.DOCUMENT_RUNNER_STARTED:
                    return { progress: 10, message: 'The document runner has started' };

                case DocumentRunnerStatus.PROPERTY_CARD_COLLECTING:
                    return { progress: 15, message: 'The property card is being collected' };

                case DocumentRunnerStatus.BOOKS_AND_PAGE_FOUND:
                    return { progress: 25, message: response[0].message.split('#')[1].split(' for')[0] };

                case DocumentRunnerStatus.DEEDS_COLLECTING:
                    return { progress: 35, message: 'The deeds are being collected' };

                case DocumentRunnerStatus.DEEDS_COLLECTED:
                    return { progress: 50, message: 'The deeds were collected' };

                case DocumentRunnerStatus.TAX_DOCUMENT_COLLECTING:
                    return { progress: 60, message: 'The tax document is being collected' };

                case DocumentRunnerStatus.PROPERTY_CARD_UPLOADING:
                    return { progress: 65, message: 'The property card is being uploading' };

                case DocumentRunnerStatus.TAX_DOCUMENT_UPLOADING:
                    return { progress: 70, message: 'The tax document is being uploading' };

                case DocumentRunnerStatus.DEEDS_UPLOADING:
                    return { progress: 75, message: 'The deeds are being uploading' };

                case DocumentRunnerStatus.NOT_FOUND:
                    toast.error('Invalid TMS / Parcel Number. Please re-enter and try again.');
                    return;

                default:
                    return { progress: 90, message: 'Your request is finalizing' };
            }
        }

        return { progress: 10, message: 'The document runner has started' };
    }

    getValidateDocumentLogs = async (organizationId: string, batchId: string): Promise<ValidateDocumentDto | undefined> => {
        const response = await this.auditLogsStore.getAuditLogs(organizationId, batchId);

        if (response && typeof response !== 'string' && response.length > 0) {
            const message = response[0].message.split('#');
            if (message.length > 0) {
                const status = message[0];

                switch (status) {
                    case ValidateDocumentStatus.VALIDATE_INITIATED:
                        return {progressStatusDto: {progress: 10, message: message[1]}};

                    case ValidateDocumentStatus.VALIDATE_PROGRESS:
                        return {progressStatusDto: {progress: 25, message: message[1]}};

                    case ValidateDocumentStatus.VALIDATE_COMPLETE:
                        return {progressStatusDto: {progress: 75, message: message[1]}};

                    case ValidateDocumentStatus.MERGING_PROGRESS:
                        return {progressStatusDto: {progress: 90, message: message[1]}};

                    case ValidateDocumentStatus.RESULT:
                        return {validateResponse: JSON.parse(message[1]), progressStatusDto: {progress: 100, message: 'Documents validated successfully !!'}};
                }
            }
        }
    }

    getSplitDocumentsLogs = async (organizationId: string, batchId: string): Promise<ProgressStatusDto> => {
        const response = await this.auditLogsStore.getAuditLogs(organizationId, batchId);

        if (response && typeof response !== 'string' && response.length > 0) {
            const message = response[0].message.split('#');
            if (message.length > 0) {
                const status = message[0];

                switch (status) {
                    case SplitDocumentsStatus.SPLIT_INITIATED:
                        return { progress: 5, message: 'Your request is being processed' };

                    case SplitDocumentsStatus.SPLIT_STARTED:
                        return { progress: 10, message: message.length > 1 ? message[1] : '' };

                    case SplitDocumentsStatus.SPLIT_PROGRESS:
                        return { progress: 25, message: message.length > 1 ? message[1].split('for pages')[0] : '' };

                    case SplitDocumentsStatus.SPLIT_DONE:
                    case SplitDocumentsStatus.SPLITTER_DOCUMENTS_UPLOADING:
                    case SplitDocumentsStatus.DOCUMENT_UPLOAD_COMPLETED:
                        return { progress: 50, message: message.length > 1 ? message[1] : '' };

                    case SplitDocumentsStatus.SPLIT_ERROR:
                        return { progress: 60, message: message.length > 1 ? message[1] : '' };

                    default:
                        return { progress: 90, message: 'Your request is finalizing' };
                }
            }
        }

        return { progress: 5, message: 'Your request is being processed' };
    }

    getMergeDocumentsLogs = async (organizationId: string, batchId: string): Promise<ProgressStatusDto> => {
        const response = await this.auditLogsStore.getAuditLogs(organizationId, batchId);

        if (response && typeof response !== 'string' && response.length > 0) {
            const message = response[0].message.split('#');
            if (message.length > 0) {
                const status = message[0];

                switch (status) {
                    case MergeDocumentsStatus.MERGE_INITIATED:
                        return { progress: 5, message: 'Your request is being processed' };

                    case MergeDocumentsStatus.MERGE_PROGRESS:
                        return { progress: 25, message: message.length > 1 ? message[1] : '' };

                    case MergeDocumentsStatus.MERGE_ERROR:
                        return { progress: 30, message: message.length > 1 ? message[1] : ''};

                    case MergeDocumentsStatus.MERGING_DOCUMENT:
                        return { progress: 40, message: message.length > 1 ? message[1] : '' };

                    case MergeDocumentsStatus.GENERATING_BOOKMARKS:
                        return { progress: 55, message: message.length > 1 ? message[1] : '' };

                    case MergeDocumentsStatus.UPLOADING_DOCUMENTS:
                        return { progress: 70, message: message.length > 1 ? message[1] : '' };

                    default:
                        return { progress: 90, message: 'Your request is finalizing' };
                }
            }
        }

        return { progress: 5, message: 'Your request is being processed' };
    }

    getMarketplaceDownloadLogs = async (organizationId: string, batchId: string): Promise<ProgressStatusDto & { downloadUrl?: string }> => {
        const response = await this.auditLogsStore.getAuditLogs(organizationId, batchId);

        if (typeof response === 'string') {
            toast.error('Failed to get download progress. Please try again.');
            return { progress: 0, message: 'Unable to check progress' };
        }

        if (response && response.length > 0) {
            const message = response[0].message.split('#');
            if (message.length > 0) {
                const status = message[0];

                switch (status) {
                    case MarketplaceOperationStatus.DOWNLOAD_INITIATED:
                        return { progress: 5, message: 'Your download request is being processed' };

                    case MarketplaceOperationStatus.DOWNLOAD_STARTED:
                        return { progress: 15, message: message.length > 1 ? message[1] : 'Download started' };

                    case MarketplaceOperationStatus.DOWNLOADING_DOCUMENT:
                        return { progress: 35, message: message.length > 1 ? message[1] : 'Download in progress' };

                    case MarketplaceOperationStatus.SPLITTING_DOCUMENT:
                        return { progress: 55, message: message.length > 1 ? message[1] : 'Splitting document' };

                    case MarketplaceOperationStatus.TEMP_UPLOAD_PROGRESS:
                        return { progress: 75, message: message.length > 1 ? message[1] : 'Uploading document' };

                    case MarketplaceOperationStatus.DOWNLOAD_COMPLETE:
                        const downloadUrl = message.length > 2 ? message[2] : undefined;
                        return {
                            progress: 90,
                            message: 'Document ready for download!',
                            downloadUrl: downloadUrl
                        };

                    case MarketplaceOperationStatus.DOWNLOAD_ERROR:
                        const errorMessage = message.length > 1 ? message[1] : 'Download failed - please try again';
                        toast.error(errorMessage);
                        return { progress: 30, message: errorMessage };

                    default:
                        return { progress: 15, message: 'Processing your document request' };
                }
            }
        }

        return { progress: 5, message: 'Your download request is being processed' };
    }

    getMarketplaceImportLogs = async (organizationId: string, batchId: string): Promise<ProgressStatusDto> => {
        const response = await this.auditLogsStore.getAuditLogs(organizationId, batchId);

        if (typeof response === 'string') {
            toast.error('Failed to get import progress. Please try again.');
            return { progress: 0, message: 'Unable to check progress' };
        }

        if (response && response.length > 0) {
            const message = response[0].message.split('#');
            if (message.length > 0) {
                const status = message[0];

                switch (status) {
                    case MarketplaceOperationStatus.IMPORT_INITIATED:
                        return { progress: 5, message: 'Your import request is being processed' };

                    case MarketplaceOperationStatus.IMPORT_STARTED:
                        return { progress: 10, message: message.length > 1 ? message[1] : 'Starting document import' };

                    case MarketplaceOperationStatus.DOWNLOADING_DOCUMENT:
                        return { progress: 25, message: message.length > 1 ? message[1] : 'Fetching document pages from archive' };

                    case MarketplaceOperationStatus.SPLITTING_DOCUMENT:
                        return { progress: 45, message: message.length > 1 ? message[1] : 'Processing document content' };

                    case MarketplaceOperationStatus.IMPORTING_DOCUMENT:
                        return { progress: 65, message: message.length > 1 ? message[1] : 'Creating document in your title' };

                    case MarketplaceOperationStatus.UPLOADING_DOCUMENT:
                        return { progress: 70, message: message.length > 1 ? message[1] : 'Uploading to your title section' };

                    case MarketplaceOperationStatus.UPDATING_TITLE:
                        return { progress: 80, message: message.length > 1 ? message[1] : 'Updating title' };

                    case MarketplaceOperationStatus.COMPLETING_IMPORT:
                        toast.success('Document imported successfully!');
                        return { progress: 90, message: 'Document imported successfully!' };

                    case MarketplaceOperationStatus.IMPORT_ERROR:
                        const errorMessage = message.length > 1 ? message[1] : 'Import failed - please try again';
                        toast.error(errorMessage);
                        return { progress: 30, message: errorMessage };

                    default:
                        return { progress: 15, message: 'Processing your import request' };
                }
            }
        }

        return { progress: 5, message: 'Your import request is being processed' };
    }
}
